import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  deleteField,
  doc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { db } from "../../firebase";

const text = (value) => String(value ?? "").trim();
const CHUNK_LIMIT = 3;
const ACTIVE_MESSAGE_LIMIT = 50;
const ACTIVE_BYTES_LIMIT = 64 * 1024;

function normalizeRole(value) {
  const role = text(value).toLowerCase();
  if (["owner", "primary", "primary_admin"].includes(role)) return "owner";
  if (["deputy", "deputy_admin"].includes(role)) return "deputy";
  return "driver";
}

function toDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatTime(value) {
  const date = toDate(value);
  return date ? `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}` : "";
}

function compressGroupPhoto(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const maxSize = 1080;
      const scale = Math.min(1, maxSize / Math.max(image.naturalWidth, image.naturalHeight));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
      const context = canvas.getContext("2d");
      context.fillStyle = "#f2f3f5";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(objectUrl);
        if (blob) resolve(blob);
        else reject(new Error("群組照片壓縮失敗。"));
      }, "image/jpeg", 0.78);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("無法讀取群組照片。"));
    };
    image.src = objectUrl;
  });
}

function createPhotoUuid() {
  const cryptoApi = window.crypto;
  if (typeof cryptoApi?.randomUUID === "function") return cryptoApi.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    return (character === "x" ? random : (random & 0x3) | 0x8).toString(16);
  });
}

function originalImageExtension(file) {
  const type = text(file?.type).toLowerCase();
  const typeExtensions = {
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
    "image/gif": "gif",
    "image/avif": "avif",
    "image/heic": "heic",
    "image/heif": "heif",
    "image/bmp": "bmp",
    "image/tiff": "tif",
  };
  if (typeExtensions[type]) return typeExtensions[type];
  const fileName = text(file?.name);
  const dotIndex = fileName.lastIndexOf(".");
  const extension = dotIndex >= 0 ? fileName.slice(dotIndex + 1).toLowerCase() : "";
  return /^[a-z0-9]{1,10}$/.test(extension) ? extension : "img";
}

async function discardUploadedPhotoObjects(paths = []) {
  const storage = getStorage();
  await Promise.all(paths.filter(Boolean).map(async (path) => {
    try {
      await deleteObject(storageRef(storage, path));
    } catch (_) {}
  }));
}

function normalizeMember(id, data = {}) {
  return {
    uid: text(data.uid || id),
    displayName: text(data.displayName || data.name || data.driverName) || "未命名成員",
    photoURL: text(data.photo?.previewURL || data.photoURL || data.photoUrl || data.avatarUrl),
    role: normalizeRole(data.role || data.memberRole || data.orgRole),
    status: text(data.status || data.memberStatus || "active").toLowerCase(),
  };
}

function normalizeMessage(id, data = {}) {
  const priority = text(data.alertLevel || data.priority || "normal");
  return { id, ...data, priority: ["normal", "important", "urgent"].includes(priority) ? priority : "normal", contentType: text(data.contentType) || "text", text: text(data.text), senderUid: text(data.senderUid), createdAt: data.createdAt || null };
}

function priorityLabel(priority) { return priority === "urgent" ? "緊急" : priority === "important" ? "重要" : "一般"; }

function initials(name) { return text(name).slice(0, 1).toUpperCase() || "聊"; }

async function readMembers(orgId) {
  const memberSnapshot = await getDocs(query(collection(db, "drivers"), where("orgId", "==", orgId)));
  return memberSnapshot.docs
    .map((item) => normalizeMember(item.id, item.data() || {}))
    .filter((member) => member.status === "active");
}

async function markInboxRead(orgId, uid, chat) {
  const bucketId = text(chat?.inboxBucketByUid?.[uid] || chat?.inboxBucketId || "bucket_0001");
  if (!orgId || !uid || !chat?.id) return;
  try {
    await updateDoc(doc(db, "orgs", orgId, "chatInboxes", uid, "buckets", bucketId), {
      [`entries.${chat.id}.unreadCount`]: 0,
      [`entries.${chat.id}.lastReadAt`]: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("[DriverManagerChatRoom] inbox read error:", error);
  }
}

async function appendChatMessage({ orgId, chatId, messageId, message }) {
  const chatRef = doc(db, "orgs", orgId, "chats", chatId);
  await runTransaction(db, async (transaction) => {
    const chatSnapshot = await transaction.get(chatRef);
    if (!chatSnapshot.exists()) throw new Error("chat_not_found");
    const chat = chatSnapshot.data() || {};
    if (!Array.isArray(chat.participantUids) || !chat.participantUids.includes(message.senderUid)) throw new Error("not_a_chat_participant");
    const ownBucketId = text(chat.inboxBucketByUid?.[message.senderUid]);
    const ownInboxRef = ownBucketId ? doc(db, "orgs", orgId, "chatInboxes", message.senderUid, "buckets", ownBucketId) : null;
    const ownInboxSnapshot = ownInboxRef ? await transaction.get(ownInboxRef) : null;
    const ownInboxEntry = ownInboxSnapshot?.exists() ? ownInboxSnapshot.data()?.entries?.[chatId] : null;
    let chunkId = text(chat.activeChunkId);
    let chunkRef = chunkId ? doc(db, "orgs", orgId, "chats", chatId, "messageChunks", chunkId) : null;
    let chunkSnapshot = chunkRef ? await transaction.get(chunkRef) : null;
    let chunk = chunkSnapshot?.exists() ? chunkSnapshot.data() || {} : null;
    const oldMessages = chunk?.messages || {};
    const oldCount = Object.keys(oldMessages).length;
    const estimatedBytes = Number(chunk?.estimatedBytes || 0);
    if (!chunk || oldCount >= ACTIVE_MESSAGE_LIMIT || estimatedBytes + JSON.stringify(message).length >= ACTIVE_BYTES_LIMIT) {
      const nextIndex = Math.max(1, Number(chat.nextChunkIndex || 1));
      chunkId = `chunk_${String(nextIndex).padStart(6, "0")}`;
      chunkRef = doc(db, "orgs", orgId, "chats", chatId, "messageChunks", chunkId);
      chunk = { orgId, chatId, chunkId, chunkIndex: nextIndex, status: "active", messageCount: 0, estimatedBytes: 0, messages: {} };
    }
    const nextMessages = { ...(chunk.messages || {}), [messageId]: message };
    const nextCount = Object.keys(nextMessages).length;
    const nextBytes = Number(chunk.estimatedBytes || 0) + JSON.stringify(message).length;
    const recentChunkIds = Array.from(new Set([...(Array.isArray(chat.recentChunkIds) ? chat.recentChunkIds : []), chunkId])).slice(-CHUNK_LIMIT);
    transaction.set(chunkRef, { ...chunk, messages: nextMessages, messageCount: nextCount, estimatedBytes: nextBytes, updatedAt: serverTimestamp() }, { merge: true });
    transaction.update(chatRef, { activeChunkId: chunkId, recentChunkIds, activeMessageCount: nextCount, nextChunkIndex: Math.max(Number(chat.nextChunkIndex || 1), Number(chunk.chunkIndex || 1) + 1), lastMessageText: message.text, lastMessageAt: message.createdAt, lastMessageSenderUid: message.senderUid, lastMessage: { contentType: message.contentType, text: message.text, senderUid: message.senderUid, createdAt: message.createdAt }, updatedAt: serverTimestamp() });
    if (ownInboxRef && ownInboxEntry) {
      transaction.update(ownInboxRef, {
        [`entries.${chatId}`]: {
          ...ownInboxEntry,
          lastMessageText: message.text,
          lastMessageAt: message.createdAt,
          lastMessageSenderUid: message.senderUid,
          unreadCount: 0,
        },
        updatedAt: serverTimestamp(),
      });
    }
  });
}

async function findInboxBucket(orgId, uid, chatId, preferredBucketId = "") {
  if (text(preferredBucketId)) return text(preferredBucketId);
  const snapshot = await getDocs(collection(db, "orgs", orgId, "chatInboxes", uid, "buckets"));
  const buckets = snapshot.docs.map((item) => ({ id: item.id, data: item.data() || {} }));
  const existing = buckets.find((bucket) => bucket.data.entries?.[chatId]);
  if (existing) return existing.id;
  const available = buckets.find((bucket) => Object.keys(bucket.data.entries || {}).length < 10);
  if (available) return available.id;
  const nextNumber = buckets.reduce((max, bucket) => Math.max(max, Number(bucket.id.replace("bucket_", "")) || 0), 0) + 1;
  return `bucket_${String(nextNumber).padStart(4, "0")}`;
}
async function removeInboxEntry(orgId, uid, chatId, bucketId = "bucket_0001") {
  try {
    await updateDoc(doc(db, "orgs", orgId, "chatInboxes", uid, "buckets", bucketId), { [`entries.${chatId}`]: deleteField(), updatedAt: serverTimestamp() });
  } catch (_) {}
}

function Icon({ name, size = 18 }) {
  const paths = { back: "m15 18-6-6 6-6", send: "m4 4 17 8-17 8 4-8-4-8Zm4 8h13", people: "M16 20a4 4 0 0 0-8 0M12 13a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm6.5 6a3.5 3.5 0 0 0-2.2-3.25M16.5 6.2a3 3 0 0 1 0 5.6", close: "M6 6l12 12M18 6 6 18", plus: "M12 5v14M5 12h14" };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={paths[name] || paths.people} /></svg>;
}

const Avatar = memo(function Avatar({ member, size = 34 }) {
  if (member?.photoURL) return <img className="dmcr-avatar" style={{ width: size, height: size }} src={member.photoURL} alt="" loading="lazy" decoding="async" />;
  return <span className="dmcr-avatar" style={{ width: size, height: size }}>{initials(member?.displayName)}</span>;
});

function MessageBubble({ message, mine, sender }) {
  const legacyVoice = message.contentType === "audio";
  const marked = message.priority !== "normal";
  return <div className={`dmcr-message-row${mine ? " is-mine" : ""}`}>
    {!mine && <Avatar member={sender} size={30} />}
    <div className={`dmcr-message-stack${mine ? " is-mine" : ""}`}>
      {!mine && <small className="dmcr-sender">{sender?.displayName || "成員"}</small>}
      {marked && <span className={`dmcr-priority-badge is-${message.priority}`}>{priorityLabel(message.priority)}</span>}
      <div className={`dmcr-bubble${mine ? " is-mine" : ""}${marked ? ` is-${message.priority}` : ""}`}>
        {legacyVoice ? <span className="dmcr-legacy-voice">語音功能已停用</span> : <span>{message.text}</span>}
      </div>
      <time>{formatTime(message.createdAt)}</time>
    </div>
  </div>;
}

function InfoPanel({ chat, members, uid, canManage, selected, setSelected, onClose, onSave, saving, onSaveProfile, profileSaving }) {
  const profileInputRef = useRef(null);
  const [profileTitle, setProfileTitle] = useState("");
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState("");

  useEffect(() => {
    if (!chat) return;
    setProfileTitle(text(chat.title));
    setProfilePhotoFile(null);
    setProfilePhotoPreview(text(chat.groupPhoto?.previewURL || chat.photoURL || chat.photoUrl));
  }, [chat?.id, chat?.title, chat?.photoURL, chat?.photoUrl]);

  useEffect(() => () => {
    if (profilePhotoPreview.startsWith("blob:")) URL.revokeObjectURL(profilePhotoPreview);
  }, [profilePhotoPreview]);

  if (!chat) return null;
  const group = chat.chatType === "group";
  const profileChanged = text(profileTitle) !== text(chat.title) || Boolean(profilePhotoFile);
  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      window.alert("請選擇圖片檔案。");
      return;
    }
    setProfilePhotoFile(file);
    setProfilePhotoPreview(URL.createObjectURL(file));
  };
  return <div className="dmcr-info-backdrop" onClick={onClose}><aside className="dmcr-info" onClick={(event) => event.stopPropagation()}>
    <div className="dmcr-info-head"><strong>{group ? "群組資訊" : "聊天室成員"}</strong><button type="button" onClick={onClose}><Icon name="close" size={18} /></button></div>
    <p className="dmcr-info-sub">{group ? "主管理員可以調整群組名稱、照片與成員。" : "一對一聊天室成員"}</p>
    {group && canManage && <section className="dmcr-profile-editor">
      <div className="dmcr-profile-heading"><strong>群組設定</strong><small>這些資訊會同步給所有成員</small></div>
      <div className="dmcr-profile-row">
        <button type="button" className="dmcr-profile-photo-button" onClick={() => profileInputRef.current?.click()} disabled={profileSaving}>
          <Avatar member={{ displayName: profileTitle, photoURL: profilePhotoPreview }} size={62} />
          <span><strong>群組照片</strong><small>點擊更換</small></span>
        </button>
        <input ref={profileInputRef} className="dmcr-profile-file" type="file" accept="image/*" onChange={handlePhotoChange} />
        <label className="dmcr-profile-name"><span>群組名稱</span><input value={profileTitle} onChange={(event) => setProfileTitle(event.target.value)} maxLength={60} placeholder="輸入群組名稱" /></label>
      </div>
      <button type="button" className="dmcr-save-profile" onClick={() => onSaveProfile({ title: text(profileTitle), file: profilePhotoFile })} disabled={profileSaving || !profileChanged}>{profileSaving ? "儲存中…" : "儲存群組資訊"}</button>
    </section>}
    <div className="dmcr-member-list">{members.map((member) => {
      const active = selected.includes(member.uid);
      const isSelf = member.uid === uid;
      return <button type="button" key={member.uid} className={`dmcr-member-choice${active ? " is-active" : ""}`} disabled={!canManage || isSelf} onClick={() => setSelected((previous) => active ? previous.filter((id) => id !== member.uid) : [...previous, member.uid])}><Avatar member={member} size={32} /><span><strong>{member.displayName}</strong><small>{member.role === "owner" ? "主管理員" : member.role === "deputy" ? "副管理員" : "成員"}</small></span><em>{active ? "已加入" : "加入"}</em></button>;
    })}</div>
    {canManage && group && <button type="button" className="dmcr-save-members" onClick={onSave} disabled={saving}>{saving ? "儲存中…" : "儲存群組成員"}</button>}
  </aside></div>;
}

export default function DriverManagerChatRoom({ user, orgId, chatId, title, onBack }) {
  const uid = text(user?.uid);
  const [chat, setChat] = useState(null);
  const [members, setMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [messagePriority, setMessagePriority] = useState("normal");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [infoOpen, setInfoOpen] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [savingMembers, setSavingMembers] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const chatRef = useMemo(() => orgId && chatId ? doc(db, "orgs", orgId, "chats", chatId) : null, [chatId, orgId]);
  const memberMap = useMemo(() => new Map(members.map((member) => [member.uid, member])), [members]);
  const currentMember = memberMap.get(uid);
  const canChat = currentMember?.status === "active" && chat?.participantUids?.includes(uid);
  const canManage = canChat && chat?.chatType === "group" && currentMember?.role === "owner";
  const displayTitle = text(chat?.title) || text(title) || "聊天室";

  useEffect(() => {
    if (!chatRef) { setChat(null); setLoading(false); return undefined; }
    setLoading(true);
    return onSnapshot(chatRef, (snapshot) => { const next = snapshot.exists() ? { id: snapshot.id, ...(snapshot.data() || {}) } : null; setChat(next); setLoading(false); }, (snapshotError) => { console.error("[DriverManagerChatRoom] chat error:", snapshotError); setError("聊天室讀取失敗。"); setLoading(false); });
  }, [chatRef]);

  useEffect(() => {
    const chunkIds = Array.from(new Set([
      ...(Array.isArray(chat?.recentChunkIds) ? chat.recentChunkIds : []),
      chat?.activeChunkId,
    ].map(text).filter(Boolean))).slice(-CHUNK_LIMIT);
    if (!orgId || !chatId || !chunkIds.length) { setMessages([]); return undefined; }
    const messageMap = new Map();
    const unsubs = chunkIds.map((chunkId) => onSnapshot(
      doc(db, "orgs", orgId, "chats", chatId, "messageChunks", chunkId),
      (snapshot) => {
        Object.entries(snapshot.data()?.messages || {}).forEach(([id, message]) => messageMap.set(id, normalizeMessage(id, message)));
        setMessages(Array.from(messageMap.values()).sort((left, right) => (toDate(left.createdAt)?.getTime() || 0) - (toDate(right.createdAt)?.getTime() || 0)));
        requestAnimationFrame(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; });
      },
      (snapshotError) => { console.error("[DriverManagerChatRoom] chunk error:", snapshotError); setError("訊息讀取失敗，請稍後再試。"); },
    ));
    return () => unsubs.forEach((unsubscribe) => unsubscribe());
  }, [chat?.activeChunkId, chat?.recentChunkIds, chatId, orgId]);

  useEffect(() => {
    if (!orgId) { setMembers([]); return undefined; }
    let alive = true;
    readMembers(orgId).then((items) => { if (alive) setMembers(items); }).catch((memberError) => { console.error("[DriverManagerChatRoom] members error:", memberError); if (alive) setError("組織成員資料讀取失敗。"); });
    return () => { alive = false; };
  }, [orgId]);

  useEffect(() => {
    if (chat) {
      setSelectedMembers(Array.isArray(chat.participantUids) ? chat.participantUids : []);
      markInboxRead(orgId, uid, chat);
    }
  }, [chat?.id, chat?.inboxBucketByUid, chat?.participantUids, orgId, uid]);

  const sendMessage = useCallback(async (message) => {
    if (!canChat || !orgId || !chatId || sending) return;
    setSending(true);
    try {
      const messageId = doc(collection(db, "orgs", orgId, "chats", chatId, "messageChunks")).id;
      await appendChatMessage({ orgId, chatId, messageId, message: { ...message, senderUid: uid, createdAt: Timestamp.now() } });
      setError("");
    } catch (sendError) {
      console.error("[DriverManagerChatRoom] send error:", sendError);
      setError("訊息傳送失敗，請稍後再試。");
      throw sendError;
    } finally { setSending(false); }
  }, [canChat, chatId, orgId, sending, uid]);

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input) return;
      input.focus();
      const end = input.value?.length || 0;
      input.setSelectionRange?.(end, end);
    });
  }, []);

  const sendText = useCallback(async () => {
    const value = text(inputText);
    if (!value) return;
    setInputText("");
    try {
      await sendMessage({ contentType: "text", text: value, alertLevel: messagePriority });
    } catch (_) {
      setInputText(value);
    } finally {
      focusInput();
    }
  }, [focusInput, inputText, messagePriority, sendMessage]);
  const saveGroupMembers = useCallback(async () => {
    if (!canManage || !chatRef || chat?.chatType !== "group") return;
    const previous = Array.isArray(chat.participantUids) ? chat.participantUids : [];
    const next = Array.from(new Set([uid, ...selectedMembers].filter(Boolean)));
    setSavingMembers(true);
    try {
      const meta = Object.fromEntries(members.filter((member) => next.includes(member.uid)).map((member) => [member.uid, { uid: member.uid, displayName: member.displayName, photoURL: member.photoURL, role: member.role }]));
      const bucketPairs = await Promise.all(next.map(async (memberUid) => [memberUid, await findInboxBucket(orgId, memberUid, chat.id, chat.inboxBucketByUid?.[memberUid])]));
      const inboxBucketByUid = Object.fromEntries(bucketPairs);
      const batch = writeBatch(db);
      batch.update(chatRef, { participantUids: next, participantMeta: meta, inboxBucketByUid, updatedAt: serverTimestamp() });
      const entry = { chatType: "group", title: chat.title || "車隊群組", participantUids: next, participantMeta: meta, lastMessageText: chat.lastMessageText || "尚無訊息", lastMessageAt: chat.lastMessageAt || null, unreadCount: 0 };
      next.forEach((memberUid) => { const bucketId = inboxBucketByUid[memberUid]; batch.set(doc(db, "orgs", orgId, "chatInboxes", memberUid, "buckets", bucketId), { orgId, uid: memberUid, bucketId, entries: { [chat.id]: { ...entry, inboxBucketId: bucketId } }, updatedAt: serverTimestamp() }, { merge: true }); });
      await batch.commit();
      await Promise.all(previous.filter((memberUid) => !next.includes(memberUid)).map((memberUid) => removeInboxEntry(orgId, memberUid, chat.id, chat.inboxBucketByUid?.[memberUid] || "bucket_0001")));
      setInfoOpen(false);
    } catch (saveError) { console.error("[DriverManagerChatRoom] save members error:", saveError); setError("群組成員更新失敗。"); } finally { setSavingMembers(false); }
  }, [canManage, chat, chatRef, members, orgId, selectedMembers, uid]);

  const saveGroupProfile = useCallback(async ({ title: nextTitle, file }) => {
    if (!canManage || !chatRef || chat?.chatType !== "group") return;
    const normalizedTitle = text(nextTitle) || text(chat.title) || "車隊群組";
    if (!file && normalizedTitle === text(chat.title)) return;
    setSavingProfile(true);
    let uploadedPhotoPaths = [];
    try {
      let photoURL = text(chat.groupPhoto?.previewURL || chat.photoURL || chat.photoUrl);
      let photoPath = text(chat.groupPhoto?.previewPath || chat.photoPath);
      let groupPhoto = chat.groupPhoto || null;
      if (file) {
        const photoId = createPhotoUuid();
        const extension = originalImageExtension(file);
        const previewPath = `orgs/${orgId}/chats/${chat.id}/groupPhotos/${photoId}/preview.jpg`;
        const originalPath = `orgs/${orgId}/chats/${chat.id}/groupPhotos/${photoId}/original.${extension}`;
        const originalContentType = text(file.type) || "application/octet-stream";
        uploadedPhotoPaths = [previewPath, originalPath];
        try {
          const previewBlob = await compressGroupPhoto(file);
          const storage = getStorage();
          const uploadResults = await Promise.allSettled([
            uploadBytes(storageRef(storage, previewPath), previewBlob, {
              contentType: "image/jpeg",
              cacheControl: "public,max-age=31536000",
            }),
            uploadBytes(storageRef(storage, originalPath), file, {
              contentType: originalContentType,
              cacheControl: "public,max-age=31536000",
            }),
          ]);
          const failedUpload = uploadResults.find((result) => result.status === "rejected");
          if (failedUpload) throw failedUpload.reason;
          const [previewURL, originalURL] = await Promise.all([
            getDownloadURL(storageRef(storage, previewPath)),
            getDownloadURL(storageRef(storage, originalPath)),
          ]);
          photoURL = previewURL;
          photoPath = previewPath;
          groupPhoto = {
            photoId,
            previewPath,
            previewURL,
            originalPath,
            originalURL,
            originalContentType,
          };
        } catch (uploadError) {
          await discardUploadedPhotoObjects(uploadedPhotoPaths);
          uploadedPhotoPaths = [];
          throw uploadError;
        }
      }

      const batch = writeBatch(db);
      const chatUpdate = { title: normalizedTitle, photoURL, photoPath, updatedAt: serverTimestamp() };
      if (file && groupPhoto) chatUpdate.groupPhoto = groupPhoto;
      batch.update(chatRef, chatUpdate);
      const participantUids = Array.isArray(chat.participantUids) ? chat.participantUids : [];
      participantUids.forEach((memberUid) => {
        const bucketId = text(chat.inboxBucketByUid?.[memberUid]);
        if (!bucketId) return;
        batch.set(
          doc(db, "orgs", orgId, "chatInboxes", memberUid, "buckets", bucketId),
          {
            entries: { [chat.id]: { title: normalizedTitle, photoURL, photoPath, groupPhotoPreviewURL: photoURL, groupPhotoId: groupPhoto?.photoId || "" } },
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
      });
      await batch.commit();
      uploadedPhotoPaths = [];
      setError("");
      setInfoOpen(false);
    } catch (profileError) {
      if (uploadedPhotoPaths.length) await discardUploadedPhotoObjects(uploadedPhotoPaths);
      console.error("[DriverManagerChatRoom] group profile save error:", profileError);
      setError("群組資訊更新失敗，請稍後再試。");
    } finally {
      setSavingProfile(false);
    }
  }, [canManage, chat, chatRef, orgId]);

  if (loading) return <div className="dmcr-root"><style>{styles}</style><div className="dmcr-state">正在載入聊天室…</div></div>;
  if (!chat || !canChat) return <div className="dmcr-root"><style>{styles}</style><div className="dmcr-state is-error"><strong>無法開啟聊天室</strong><span>目前帳號沒有這個聊天室的有效權限。</span><button type="button" onClick={onBack}>返回聊天室</button></div></div>;

  return <div className="dmcr-root"><style>{styles}</style>
    {infoOpen && <InfoPanel chat={chat} members={members} uid={uid} canManage={canManage} selected={selectedMembers} setSelected={setSelectedMembers} onClose={() => setInfoOpen(false)} onSave={saveGroupMembers} saving={savingMembers} onSaveProfile={saveGroupProfile} profileSaving={savingProfile} />}
    <header className="dmcr-header"><button type="button" className="dmcr-back" onClick={onBack}><Icon name="back" size={21} /></button><Avatar member={{ displayName: displayTitle, photoURL: chat?.photoURL || chat?.photoUrl }} size={40} /><div className="dmcr-header-copy"><strong>{displayTitle}</strong><span>{chat.chatType === "group" ? `${chat.participantUids?.length || 0} 位成員` : "一對一聊天室"}</span></div><button type="button" className="dmcr-info-button" onClick={() => setInfoOpen(true)}><Icon name="people" size={19} /></button></header>
    {error && <div className="dmcr-error">{error}</div>}
    <main className="dmcr-messages" ref={listRef}>{messages.length ? messages.map((message) => <MessageBubble key={message.id} message={message} mine={message.senderUid === uid} sender={memberMap.get(message.senderUid)} />) : <div className="dmcr-empty"><Icon name="people" size={30} /><strong>尚未有訊息</strong><span>傳送第一則訊息，開始這段對話。</span></div>}</main>
    <footer className="dmcr-composer"><select className={`dmcr-priority-select is-${messagePriority}`} value={messagePriority} onChange={(event) => setMessagePriority(event.target.value)} aria-label="訊息標記"><option value="normal">一般</option><option value="important">重要</option><option value="urgent">緊急</option></select><input ref={inputRef} value={inputText} onChange={(event) => setInputText(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); sendText(); } }} placeholder={canChat ? "輸入訊息…" : "目前無法傳送訊息"} disabled={!canChat || sending} /><button type="button" className="dmcr-send" onClick={sendText} disabled={!text(inputText) || sending}><Icon name="send" size={17} /></button></footer>
  </div>;
}

const styles = `
.dmcr-root.dmcr-root{height:100vh;height:100dvh;max-height:100vh;max-height:100dvh;min-height:0;margin:-22px;padding:0;overflow:hidden;box-sizing:border-box;border:1px solid rgba(148,163,184,.12);border-radius:24px;background:#fff;box-shadow:0 12px 28px rgba(15,23,42,.05)}
.dmcr-root .dmcr-header{padding-left:22px;padding-right:22px}
.dmcr-root .dmcr-messages{padding-left:22px;padding-right:22px}
.dmcr-root .dmcr-composer{padding-left:22px;padding-right:22px}
.dmcr-root .dmcr-error{margin-left:22px;margin-right:22px}
@media(max-width:680px){.dmcr-root.dmcr-root{height:100vh;height:100dvh;max-height:100vh;max-height:100dvh;margin:-22px}.dmcr-root .dmcr-header{padding-left:15px;padding-right:15px}.dmcr-root .dmcr-messages{padding-left:15px;padding-right:15px}.dmcr-root .dmcr-composer{padding-left:15px;padding-right:15px}.dmcr-root .dmcr-error{margin-left:15px;margin-right:15px}}
.dmcr-root{display:flex;flex-direction:column;height:calc(100vh - 128px);max-height:calc(100vh - 128px);min-height:0;overflow:hidden;background:#f6f8fa;color:#17212b;font-family:Inter,"Noto Sans TC",-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}.dmcr-root *{box-sizing:border-box}.dmcr-root button,.dmcr-root input{font:inherit}.dmcr-root button{cursor:pointer}.dmcr-header{display:flex;align-items:center;flex:0 0 auto;gap:11px;min-height:76px;padding:12px 26px;border-bottom:1px solid #e6ebef;background:#fff}.dmcr-back,.dmcr-info-button{width:36px;height:36px;display:grid;place-items:center;border:1px solid #e2e8ec;border-radius:10px;background:#fff;color:#586873}.dmcr-info-button{margin-left:auto;border:0;background:#f2f6f8}.dmcr-avatar{display:grid;place-items:center;flex:0 0 auto;border-radius:13px;background:#e9f2fb;color:#247bd1;font-size:15px;font-weight:950;object-fit:cover}.dmcr-header-copy{display:flex;flex-direction:column;min-width:0;flex:1}.dmcr-header-copy strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:16px}.dmcr-header-copy span{margin-top:4px;color:#87949c;font-size:11px}.dmcr-error{flex:0 0 auto;margin:12px 26px 0;padding:9px 11px;border:1px solid #f0cfca;border-radius:9px;background:#fff5f2;color:#a34d47;font-size:12px}.dmcr-messages{display:flex;flex:1 1 auto;flex-direction:column;gap:14px;min-height:0;overflow-y:auto;overscroll-behavior:contain;scrollbar-gutter:stable;padding:22px 28px 28px}.dmcr-message-row{display:flex;align-items:flex-end;gap:8px;max-width:min(720px,88%)}.dmcr-message-row.is-mine{align-self:flex-end;justify-content:flex-end}.dmcr-message-stack{display:flex;flex-direction:column;align-items:flex-start;min-width:0}.dmcr-message-stack.is-mine{align-items:flex-end}.dmcr-priority-badge{align-self:flex-start;margin:0 0 4px 2px;padding:2px 6px;border-radius:999px;font-size:9px;font-weight:900}.dmcr-priority-badge.is-important{background:#fff0c9;color:#a66300}.dmcr-priority-badge.is-urgent{background:#ffe0df;color:#b33e3e}.dmcr-sender{margin:0 0 4px 2px;color:#73818a;font-size:10px;font-weight:800}.dmcr-bubble{max-width:100%;padding:10px 13px;border:1px solid #e2e8ec;border-radius:15px 15px 15px 4px;background:#fff;color:#33434d;font-size:13px;line-height:1.6;overflow-wrap:anywhere}.dmcr-bubble.is-important{border-color:#f0c36d;background:#fff8e8}.dmcr-bubble.is-urgent{border-color:#eb9b9b;background:#fff2f1}.dmcr-bubble.is-mine{border-color:#247bd1;background:#247bd1;color:#fff;border-radius:15px 15px 4px 15px}.dmcr-legacy-voice{color:#8b979e;font-size:12px}.dmcr-message-stack time{margin-top:4px;color:#9aa5ac;font-size:10px}.dmcr-empty,.dmcr-state{display:flex;align-items:center;justify-content:center;flex:1;flex-direction:column;gap:8px;color:#8b979e;font-size:12px}.dmcr-empty strong{margin-top:4px;color:#5f6e77;font-size:14px}.dmcr-empty span{color:#9aa5ac}.dmcr-composer{display:flex;align-items:center;flex:0 0 auto;position:sticky;bottom:0;z-index:5;gap:8px;padding:13px 26px 17px;border-top:1px solid #e6ebef;background:#fff}.dmcr-priority-select{height:40px;padding:0 7px;border:1px solid #e0e7eb;border-radius:11px;background:#fff;color:#52636d;font-size:11px;font-weight:800}.dmcr-priority-select.is-important{border-color:#f4c36a;color:#a66300}.dmcr-priority-select.is-urgent{border-color:#ee9c9c;color:#b33e3e}.dmcr-composer input{height:42px;min-width:0;flex:1;padding:0 13px;border:1px solid #e0e7eb;border-radius:12px;outline:0;background:#fafbfc;color:#25343d;font-size:13px}.dmcr-composer input:focus{border-color:#247bd1;box-shadow:0 0 0 3px rgba(36,123,209,.1)}.dmcr-info-backdrop{position:fixed;inset:0;z-index:50;display:flex;justify-content:flex-end;background:rgba(15,23,42,.24)}.dmcr-info{width:min(390px,92vw);height:100%;padding:24px 20px;background:#fff;box-shadow:-12px 0 36px rgba(15,23,42,.18);overflow-y:auto}.dmcr-info-head{display:flex;align-items:center;justify-content:space-between;font-size:17px}.dmcr-info-head button{width:34px;height:34px;display:grid;place-items:center;border:0;border-radius:10px;background:#f3f6f8;color:#53636d}.dmcr-info-sub{margin:8px 0 18px;color:#84919a;font-size:12px}.dmcr-profile-editor{margin-bottom:18px;padding:13px;border:1px solid #e2e8ec;border-radius:16px;background:#f8fafc}.dmcr-profile-heading{display:flex;flex-direction:column;gap:3px;margin-bottom:11px}.dmcr-profile-heading strong{font-size:13px}.dmcr-profile-heading small{color:#8b989f;font-size:10px}.dmcr-profile-row{display:flex;flex-direction:column;gap:11px}.dmcr-profile-photo-button{display:flex;align-items:center;gap:11px;width:100%;padding:8px;border:1px solid #e2e8ec;border-radius:14px;background:#fff;color:#33434d;text-align:left}.dmcr-profile-photo-button:disabled{opacity:.55;cursor:default}.dmcr-profile-photo-button>span{display:flex;flex-direction:column;gap:3px}.dmcr-profile-photo-button strong{font-size:12px}.dmcr-profile-photo-button small{color:#8b989f;font-size:10px}.dmcr-profile-file{display:none}.dmcr-profile-name{display:flex;flex-direction:column;gap:6px;color:#71808a;font-size:10px;font-weight:800}.dmcr-profile-name input{height:39px;width:100%;padding:0 10px;border:1px solid #e0e7eb;border-radius:10px;outline:0;background:#fff;color:#25343d;font-size:12px}.dmcr-profile-name input:focus{border-color:#247bd1;box-shadow:0 0 0 3px rgba(36,123,209,.1)}.dmcr-save-profile{width:100%;height:38px;margin-top:11px;border:0;border-radius:11px;background:#17212b;color:#fff;font-size:11px;font-weight:850}.dmcr-save-profile:disabled{cursor:default;opacity:.42}.dmcr-member-list{display:grid;gap:8px}.dmcr-member-choice{display:flex;align-items:center;gap:9px;width:100%;padding:9px;border:1px solid #e5ebef;border-radius:12px;background:#fff;text-align:left}.dmcr-member-choice.is-active{border-color:#b8d9f2;background:#f3f9fd}.dmcr-member-choice:disabled{cursor:default}.dmcr-member-choice>span{display:flex;flex:1;min-width:0;flex-direction:column}.dmcr-member-choice strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:12px}.dmcr-member-choice small{margin-top:3px;color:#8b989f;font-size:10px}.dmcr-member-choice em{font-style:normal;color:#7a8a94;font-size:10px}.dmcr-save-members{width:100%;height:42px;margin-top:18px;border:0;border-radius:12px;background:#247bd1;color:#fff;font-size:13px;font-weight:850}@media(max-width:680px){.dmcr-root{height:calc(100dvh - 92px);max-height:calc(100dvh - 92px)}.dmcr-header{padding:10px 15px}.dmcr-messages{padding:18px 15px 22px}.dmcr-composer{padding:10px 15px 13px}.dmcr-message-row{max-width:94%}.dmcr-error{margin:10px 15px 0}}
  `;
